import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ofActionSuccessful, Store, Actions } from '@ngxs/store';
import { DirectivesModule } from '@shared/directives/directives.module';
import { TaskActions } from '@tasks/state/actions';
import { Task, TaskList } from '@tasks/tasks.types';
import { fromEvent, take } from 'rxjs';
import { TsButtonModule } from 'tsui';

@Component({
  selector: 'app-task-details-subtasks',
  standalone: true,
  imports: [
    CommonModule,
    MatDividerModule,
    DragDropModule,
    TsButtonModule,
    MatLegacyFormFieldModule,
    MatLegacyInputModule,
    ReactiveFormsModule,
    MatIconModule,
    ReactiveFormsModule,
    FormsModule,
    DirectivesModule,
    MatLegacyButtonModule,
    MatLegacyProgressSpinnerModule,
  ],
  template: `
    <!-- Subtasks -->
    <p class="text-base font-medium mt-3">Subtasks</p>
    <mat-divider role="separator" class="mat-divider mt-1 mat-divider-horizontal" aria-orientation="horizontal">
    </mat-divider>
    <div class="flex flex-col overflow-auto mb-3">
      <div class="tasks-table">
        <div class="body" cdkDropList [cdkDropListData]="subTasks" (cdkDropListDropped)="subtaskDropped($event)">
          <div class="item" *ngFor="let task of subTasks; let index = index" cdkDrag>
            <ng-container *ngIf="!task?.editing">
              <div class="item__title">{{ task?.title }}</div>
              <div class="item__actions">
                <button
                  mat-icon-button
                  class="delete-task"
                  (click)="subtaskEdit(task)"
                  mat-flat-ts-button
                  [appAuthorize]="canEditTask"
                  denyAction="disable"
                >
                  <mat-icon [svgIcon]="'create'"></mat-icon>
                </button>
                <button
                  mat-icon-button
                  class="delete-task"
                  (click)="deleteSubtask(task?.id)"
                  mat-flat-ts-button
                  [appAuthorize]="canEditTask"
                  denyAction="disable"
                >
                  <mat-icon [svgIcon]="'delete'"></mat-icon>
                </button>
                <mat-icon
                  [appAuthorize]="canEditTask"
                  denyAction="disable"
                  cdkDragHandle
                  [svgIcon]="'drag_indicator'"
                ></mat-icon>
              </div>
            </ng-container>
            <ng-container *ngIf="task?.editing">
              <div class="item__edit-input">
                <mat-form-field class="w-full treo-mat-no-subscript">
                  <input matInput [(ngModel)]="task.title" #subTaskInputTitle [ngModelOptions]="{ standalone: true }" />
                  <ts-button
                    [icon]="'cancel'"
                    [type]="'icon'"
                    [color]="'basic'"
                    data-dd-action-name="Tasks - Details - Edit Subtask"
                    (click)="cancelSubtaskEditing(task)"
                  ></ts-button>
                  <ts-button
                    class="ml-2"
                    [icon]="'save'"
                    [type]="'icon'"
                    [color]="'basic'"
                    data-dd-action-name="Tasks - Details - Save Subtask"
                    (click)="saveSubtask(task)"
                    [debounceTime]="200"
                  ></ts-button>
                </mat-form-field>
              </div>
            </ng-container>
          </div>
          <div class="row mt-2">
            <div>
              <ts-button
                [icon]="'add'"
                [type]="'flat'"
                [color]="'basic'"
                (click)="newSubtask()"
                [disabled]="addingSubtask"
                [loading]="addingSubtask"
                [debounceTime]="200"
                mat-flat-ts-button
                [appAuthorize]="canEditTask"
                data-dd-action-name="Tasks - Details - New Subtask"
                denyAction="disable"
              >
                Add Subtask
              </ts-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrl: './task-details-subtasks.component.scss',
})
export class TaskDetailsSubtasksComponent implements OnInit {
  private _subTasks: Task[];
  @Input() canEditTask: boolean;
  @Input()
  set subTasks(tasks: Task[]) {
    this._subTasks = tasks;
    if (this.editLastSubtask) {
      this.editLastSubtask = false;
      this.addingSubtask = false;
      if (this._subTasks?.length) {
        const lastSubtask = this._subTasks[this._subTasks.length - 1];
        this.subtaskEdit(lastSubtask);
      }
    }
  }

  get subTasks(): Task[] {
    return this._subTasks;
  }
  @Input() taskListId: number;

  @Output() saveAndCreate: EventEmitter<{ task: Task; createNew: boolean }> = new EventEmitter<{
    task: Task;
    createNew: boolean;
  }>();
  @Output() createSubtask: EventEmitter<void> = new EventEmitter<void>();

  store = inject(Store);
  changeDetectorRef = inject(ChangeDetectorRef);
  actions$ = inject(Actions);

  taskList: TaskList;
  editingSubtaskId: number;
  addingSubtask = false;
  editLastSubtask = false;

  @ViewChild('subTaskInputTitle')
  subTaskInputTitle: ElementRef;

  ngOnInit(): void {}

  subtaskDropped(event: CdkDragDrop<Task[]>): void {
    if (!this.canEditTask) {
      return;
    }

    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    this.resetSubtaskOrder(event.container.data);
  }

  resetSubtaskOrder(tasks: Task[]): void {
    if (!this.canEditTask) {
      return;
    }
    let index = 0;
    for (let task of tasks) {
      task.rank = index;
      index += 1;
    }
    // TODO MJR
    this.store.dispatch(new TaskActions.ReorderTasks(this.taskListId, tasks));
  }

  subtaskEdit(task: Task): void {
    if (!this.canEditTask) {
      return;
    }
    task.editing = true;
    this.editingSubtaskId = task.id;
    this.subTaskTitleFocus(task);
  }

  // Apply focus on subtask input after a change detection.
  private subTaskTitleFocus(task: Task): void {
    this.changeDetectorRef.detectChanges();
    if (task.editing) {
      this.subTaskInputTitle?.nativeElement.focus();
      // fromEvent(this.subTaskInputTitle?.nativeElement, 'blur')
      //   .pipe(take(1))
      //   .subscribe(() => this.saveSubtask(task));
    }
  }

  saveSubtask(editedTask: Task): void {
    this.saveAndCreate.emit({ task: editedTask, createNew: false });
    editedTask.editing = false;
  }

  newSubtask(): void {
    if (!this.canEditTask) {
      return;
    }

    this.editLastSubtask = true;
    const currentSubtaskEditing = this._subTasks.find((task) => task.id === this.editingSubtaskId);
    if (this.editingSubtaskId) {
      this.saveAndCreate.emit({ task: currentSubtaskEditing, createNew: true });
      this.editingSubtaskId = undefined;
      this.addingSubtask = true;
    } else {
      this.createSubtask.emit();
    }
  }

  cancelSubtaskEditing(task: Task): void {
    task.editing = false;
  }

  deleteSubtask(subtaskId: number): void {
    if (subtaskId === this.editingSubtaskId) {
      this.editingSubtaskId = undefined;
    }

    this.store.dispatch(new TaskActions.DeleteTask(this.taskListId, subtaskId, null));
  }
}
